import {
  AttachmentCategory,
  type AttachmentCreateFileDto,
  type AttachmentCreateUrlDto,
  type AttachmentSelectDto,
  type AttachmentUpdateDto,
  type AttachmentUploadSelectDto,
  type AttachmentsSelectDto,
  type InlineAttachmentCreateDto,
  type PendingUploadDto,
} from '@@types/api.types';
import { useAuth0 } from '@auth0/auth0-react';
import { ApiErrorWrapper, useApi } from '@hooks/api.hook';
import { errorToast } from '@hooks/toast.hook';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type AttachmentUploadFailurePhase, captureAttachmentUploadFailure } from '@utils/sentry.util';
import { type AttachmentTargetType, queryKeys } from './keys';
import { type UserRoleEndpoint, useGetCurrentUser, useGetUserSelectedCompanyId } from './users.query';

export type { AttachmentTargetType } from './keys';

export type AttachmentTargetParams = {
  targetId: string;
  targetType: AttachmentTargetType;
};

export type BufferedAttachment = {
  clientId: string;
  //
  file?: File;
} & InlineAttachmentCreateDto;

type AttachmentMutationContext = AttachmentTargetParams & Pick<AttachmentCreateFileDto, 'category'>;

type AttachmentUploadParams = AttachmentTargetParams &
  Omit<AttachmentCreateFileDto, 'contentType' | 'size'> & {
    file: File;
  };

type AttachmentCreateUrlParams = AttachmentTargetParams & AttachmentCreateUrlDto;

type AttachmentDeleteParams = AttachmentMutationContext & Pick<AttachmentSelectDto, 'id'>;

type AttachmentFeatureParams = {
  attachmentId: string;
  technologyId: string;
};

type AttachmentUpdateParams = AttachmentMutationContext & AttachmentUpdateDto & Pick<AttachmentSelectDto, 'id'>;

type BufferedAttachmentErrorHandler = (error: { message: string; title: string }) => void;

type AttachmentUploadFailureContext = {
  attachmentId?: string;
  category?: AttachmentCategory;
  contentType?: string;
  fileSize?: number;
  phase: AttachmentUploadFailurePhase;
  status?: number;
  statusText?: string;
} & AttachmentTargetParams;

const ATTACHMENT_UPLOAD_GENERIC_ERROR_MESSAGE = 'Failed to save attachment.';
const COMPLETE_UPLOAD_RETRY_DELAY_MS = 1000;

const delay = async (milliseconds: number): Promise<void> => {
  await new Promise((resolve) => {
    setTimeout(resolve, milliseconds);
  });
};

const toError = (error: unknown): Error => {
  if (error instanceof Error) {
    return error;
  }

  return new Error('Unknown attachment upload error');
};

const toGenericAttachmentUploadError = (error: unknown): ApiErrorWrapper => {
  return new ApiErrorWrapper({
    errors: [{ message: ATTACHMENT_UPLOAD_GENERIC_ERROR_MESSAGE, statusCode: 500 }],
    originalError: toError(error),
  });
};

const reportAttachmentUploadError = (params: {
  context: AttachmentUploadFailureContext;
  error: unknown;
}): Error => {
  const { context, error } = params;
  const normalizedError = toError(error);

  captureAttachmentUploadFailure({
    attachmentId: context.attachmentId,
    category: context.category,
    contentType: context.contentType,
    error: normalizedError,
    fileSize: context.fileSize,
    phase: context.phase,
    status: context.status,
    statusText: context.statusText,
    targetId: context.targetId,
    targetType: context.targetType,
  });

  return normalizedError;
};

const notifyBufferedUploadFailure = (params: {
  message: string;
  onError?: BufferedAttachmentErrorHandler;
  title: string;
}): void => {
  const { message, onError, title } = params;

  if (onError) {
    onError({ message, title });
    return;
  }

  errorToast({ description: message });
};

// ---------------------------------------------------------------------------
// Shared auth check used by all GET hooks
// ---------------------------------------------------------------------------

const useAttachmentQueryEnabled = (hasTargetId: boolean): boolean => {
  const { isAuthenticated } = useAuth0();
  const { isInnovator } = useGetCurrentUser();
  const selectedCompanyId = useGetUserSelectedCompanyId();

  return Boolean(isAuthenticated) && Boolean(isInnovator) && hasTargetId && Boolean(selectedCompanyId);
};

const productAttachmentSupportedRoles: UserRoleEndpoint[] = ['expert', 'innovator', 'intel'];
const technologyAttachmentSupportedRoles: UserRoleEndpoint[] = ['expert', 'innovator', 'intel'];

// ---------------------------------------------------------------------------
// Specific GET hooks – each self-contained with its own path & query key
// ---------------------------------------------------------------------------

export const useInnovatorGetAnswerAttachments = (params: {
  answerId: string;
  category?: AttachmentCategory;
}): ReturnType<typeof useQuery<AttachmentSelectDto[], ApiErrorWrapper>> => {
  const { answerId, category } = params;
  const { apiGet } = useApi();
  const enabled = useAttachmentQueryEnabled(Boolean(answerId));

  return useQuery<AttachmentSelectDto[], ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      const result = await apiGet<AttachmentsSelectDto>({
        path: `/v1/innovator/answers/${answerId}/attachments`,
        query: { ...(category && { category }) },
      });
      return result.attachments;
    },
    queryKey: enabled
      ? queryKeys.attachments.getAnswerAttachments({ answerId, category })
      : queryKeys.placeholders.default,
  });
};

export const useInnovatorGetProductAttachments = (params: {
  category?: AttachmentCategory;
  productId: string;
}): ReturnType<typeof useQuery<AttachmentSelectDto[], ApiErrorWrapper>> => {
  const { category, productId } = params;
  const { apiGet } = useApi();
  const enabled = useAttachmentQueryEnabled(Boolean(productId));

  return useQuery<AttachmentSelectDto[], ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      const result = await apiGet<AttachmentsSelectDto>({
        path: `/v1/innovator/products/${productId}/attachments`,
        query: { ...(category && { category }) },
      });
      return result.attachments;
    },
    queryKey: enabled
      ? queryKeys.attachments.getProductAttachments({ category, productId })
      : queryKeys.placeholders.default,
  });
};

export const useGetProductAttachments = (params: {
  category?: AttachmentCategory;
  productId: string;
}): ReturnType<typeof useQuery<AttachmentSelectDto[], ApiErrorWrapper>> => {
  const { category, productId } = params;
  const { apiGet } = useApi();
  const { isAuthenticated } = useAuth0();
  const { roleEndpoint } = useGetCurrentUser();
  const selectedCompanyId = useGetUserSelectedCompanyId();
  const hasAccess = Boolean(roleEndpoint && productAttachmentSupportedRoles.includes(roleEndpoint));
  const requiresCompanyContext = roleEndpoint === 'innovator';
  const enabled = Boolean(isAuthenticated && hasAccess && productId && (!requiresCompanyContext || selectedCompanyId));

  return useQuery<AttachmentSelectDto[], ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      if (!roleEndpoint || !productAttachmentSupportedRoles.includes(roleEndpoint)) {
        throw new Error('User role not supported for product attachment access');
      }

      const result = await apiGet<AttachmentsSelectDto>({
        path: `/v1/${roleEndpoint}/products/${productId}/attachments`,
        query: { ...(category && { category }) },
      });
      return result.attachments;
    },
    queryKey: enabled
      ? queryKeys.attachments.getProductAttachments({ category, productId })
      : queryKeys.placeholders.default,
  });
};

export const useInnovatorGetTechnologyAttachments = (params: {
  category?: AttachmentCategory;
  technologyId: string;
}): ReturnType<typeof useQuery<AttachmentSelectDto[], ApiErrorWrapper>> => {
  const { category, technologyId } = params;
  const { apiGet } = useApi();
  const enabled = useAttachmentQueryEnabled(Boolean(technologyId));

  return useQuery<AttachmentSelectDto[], ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      const result = await apiGet<AttachmentsSelectDto>({
        path: `/v1/innovator/technologies/${technologyId}/attachments`,
        query: { ...(category && { category }) },
      });
      return result.attachments;
    },
    queryKey: enabled
      ? queryKeys.attachments.getTechnologyAttachments({ category, technologyId })
      : queryKeys.placeholders.default,
  });
};

export const useGetTechnologyAttachments = (params: {
  category?: AttachmentCategory;
  technologyId: string;
}): ReturnType<typeof useQuery<AttachmentSelectDto[], ApiErrorWrapper>> => {
  const { category, technologyId } = params;
  const { apiGet } = useApi();
  const { isAuthenticated } = useAuth0();
  const { roleEndpoint } = useGetCurrentUser();
  const selectedCompanyId = useGetUserSelectedCompanyId();
  const hasAccess = Boolean(roleEndpoint && technologyAttachmentSupportedRoles.includes(roleEndpoint));
  const requiresCompanyContext = roleEndpoint === 'innovator';
  const enabled = Boolean(
    isAuthenticated && hasAccess && technologyId && (!requiresCompanyContext || selectedCompanyId),
  );

  return useQuery<AttachmentSelectDto[], ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      if (!roleEndpoint || !technologyAttachmentSupportedRoles.includes(roleEndpoint)) {
        throw new Error('User role not supported for technology attachment access');
      }

      const result = await apiGet<AttachmentsSelectDto>({
        path: `/v1/${roleEndpoint}/technologies/${technologyId}/attachments`,
        query: { ...(category && { category }) },
      });
      return result.attachments;
    },
    queryKey: enabled
      ? queryKeys.attachments.getTechnologyAttachments({ category, technologyId })
      : queryKeys.placeholders.default,
  });
};

// ---------------------------------------------------------------------------
// Mutation helpers (kept generic – mutations are imperative with explicit params)
// ---------------------------------------------------------------------------

const getAttachmentBasePath = (params: AttachmentTargetParams): string => {
  const { targetId, targetType } = params;

  switch (targetType) {
    case 'answer':
      return `/v1/innovator/answers/${targetId}/attachments`;
    case 'product':
      return `/v1/innovator/products/${targetId}/attachments`;
    case 'technology':
      return `/v1/innovator/technologies/${targetId}/attachments`;
  }
};

const invalidateAttachmentListQuery = (
  params: AttachmentMutationContext,
  queryClient: ReturnType<typeof useQueryClient>,
): void => {
  const { category, targetId, targetType } = params;

  switch (targetType) {
    case 'answer':
      queryClient.invalidateQueries({
        queryKey: queryKeys.attachments.getAnswerAttachments({ answerId: targetId, category }),
      });
      break;
    case 'product':
      queryClient.invalidateQueries({
        queryKey: queryKeys.attachments.getProductAttachments({ category, productId: targetId }),
      });
      break;
    case 'technology':
      queryClient.invalidateQueries({
        queryKey: queryKeys.attachments.getTechnologyAttachments({ category, technologyId: targetId }),
      });
      break;
  }
};

// ---------------------------------------------------------------------------
// Mutation hooks
// ---------------------------------------------------------------------------

export const useInnovatorCreateAttachmentUrl = () => {
  const { apiPost } = useApi();
  const { isInnovator } = useGetCurrentUser();
  const queryClient = useQueryClient();

  const onSuccess = (_: AttachmentSelectDto, variables: AttachmentCreateUrlParams) => {
    invalidateAttachmentListQuery(
      {
        category: variables.category,
        targetId: variables.targetId,
        targetType: variables.targetType,
      },
      queryClient,
    );
  };

  return useMutation<AttachmentSelectDto, ApiErrorWrapper, AttachmentCreateUrlParams>({
    mutationFn: async (data) => {
      if (!isInnovator) {
        throw new Error('Only innovators can create attachments.');
      }

      const { targetId, targetType, ...payload } = data;

      return await apiPost<AttachmentSelectDto>({
        body: payload,
        path: `${getAttachmentBasePath({ targetId, targetType })}/url`,
      });
    },
    onSuccess,
  });
};

export const useInnovatorUploadAttachmentFile = () => {
  const { apiDelete: apiDeleteWithoutToast } = useApi({ showToastOnError: false });
  const { apiPost } = useApi();
  const { isInnovator } = useGetCurrentUser();
  const queryClient = useQueryClient();

  const onSuccess = (_: AttachmentSelectDto, variables: AttachmentUploadParams) => {
    invalidateAttachmentListQuery(
      {
        category: variables.category,
        targetId: variables.targetId,
        targetType: variables.targetType,
      },
      queryClient,
    );
  };

  return useMutation<AttachmentSelectDto, ApiErrorWrapper, AttachmentUploadParams>({
    mutationFn: async (data) => {
      if (!isInnovator) {
        throw new Error('Only innovators can create attachments.');
      }

      const { category, description, file, targetId, targetType, title } = data;
      const contentType = file.type || 'application/octet-stream';
      const basePath = getAttachmentBasePath({ targetId, targetType });
      const cleanupAttachmentAfterS3Failure = async (attachmentId: string): Promise<void> => {
        try {
          await apiDeleteWithoutToast({
            path: `${basePath}/${attachmentId}`,
          });
        } catch (error) {
          reportAttachmentUploadError({
            context: {
              attachmentId,
              category,
              contentType,
              fileSize: file.size,
              phase: 'cleanup_after_s3_failure',
              targetId,
              targetType,
            },
            error,
          });
        }
      };

      let upload: AttachmentUploadSelectDto;

      try {
        upload = await apiPost<AttachmentUploadSelectDto>({
          body: {
            category,
            contentType,
            description,
            size: file.size,
            title,
          },
          path: `${basePath}/file`,
        });
      } catch (error) {
        reportAttachmentUploadError({
          context: {
            category,
            contentType,
            fileSize: file.size,
            phase: 'request_upload_url',
            targetId,
            targetType,
          },
          error,
        });

        throw toGenericAttachmentUploadError(error);
      }

      let s3Response: Response;

      try {
        s3Response = await window.fetch(upload.uploadUrl, {
          body: file,
          headers: { 'Content-Type': contentType },
          method: 'PUT',
        });
      } catch (error) {
        reportAttachmentUploadError({
          context: {
            attachmentId: upload.id,
            category,
            contentType,
            fileSize: file.size,
            phase: 's3_put',
            targetId,
            targetType,
          },
          error,
        });

        await cleanupAttachmentAfterS3Failure(upload.id);
        throw toGenericAttachmentUploadError(error);
      }

      if (!s3Response.ok) {
        const s3UploadError = new Error(`S3 upload failed: ${s3Response.status} ${s3Response.statusText}`);

        reportAttachmentUploadError({
          context: {
            attachmentId: upload.id,
            category,
            contentType,
            fileSize: file.size,
            phase: 's3_put',
            status: s3Response.status,
            statusText: s3Response.statusText,
            targetId,
            targetType,
          },
          error: s3UploadError,
        });

        await cleanupAttachmentAfterS3Failure(upload.id);
        throw toGenericAttachmentUploadError(s3UploadError);
      }

      try {
        return await apiPost<AttachmentSelectDto>({
          path: `${basePath}/${upload.id}/complete`,
        });
      } catch {
        await delay(COMPLETE_UPLOAD_RETRY_DELAY_MS);

        try {
          return await apiPost<AttachmentSelectDto>({
            path: `${basePath}/${upload.id}/complete`,
          });
        } catch (error) {
          reportAttachmentUploadError({
            context: {
              attachmentId: upload.id,
              category,
              contentType,
              fileSize: file.size,
              phase: 'complete_upload',
              targetId,
              targetType,
            },
            error,
          });

          throw toGenericAttachmentUploadError(error);
        }
      }
    },
    onSuccess,
  });
};

export const useInnovatorUpdateAttachment = () => {
  const { apiPatch } = useApi();
  const { isInnovator } = useGetCurrentUser();
  const queryClient = useQueryClient();

  const onSuccess = (_: AttachmentSelectDto, variables: AttachmentUpdateParams) => {
    invalidateAttachmentListQuery(
      {
        category: variables.category,
        targetId: variables.targetId,
        targetType: variables.targetType,
      },
      queryClient,
    );
  };

  return useMutation<AttachmentSelectDto, ApiErrorWrapper, AttachmentUpdateParams>({
    mutationFn: async (data) => {
      if (!isInnovator) {
        throw new Error('Only innovators can update attachments.');
      }

      const { category, id, targetId, targetType, ...payload } = data;

      return await apiPatch<AttachmentSelectDto>({
        body: payload,
        path: `${getAttachmentBasePath({ targetId, targetType })}/${id}`,
      });
    },
    onSuccess,
  });
};

export const useInnovatorDeleteAttachment = () => {
  const { apiDelete } = useApi();
  const { isInnovator } = useGetCurrentUser();
  const queryClient = useQueryClient();

  const onSuccess = (_: void, variables: AttachmentDeleteParams) => {
    invalidateAttachmentListQuery(
      {
        category: variables.category,
        targetId: variables.targetId,
        targetType: variables.targetType,
      },
      queryClient,
    );
  };

  return useMutation<void, ApiErrorWrapper, AttachmentDeleteParams>({
    mutationFn: async (data) => {
      if (!isInnovator) {
        throw new Error('Only innovators can delete attachments.');
      }

      await apiDelete({
        path: `${getAttachmentBasePath({ targetId: data.targetId, targetType: data.targetType })}/${data.id}`,
      });
    },
    onSuccess,
  });
};

export const useInnovatorFeatureTechnologyImage = () => {
  const { apiPost } = useApi();
  const { isInnovator } = useGetCurrentUser();
  const queryClient = useQueryClient();

  const onSuccess = (_: AttachmentSelectDto, variables: AttachmentFeatureParams) => {
    invalidateAttachmentListQuery(
      {
        category: AttachmentCategory.TECHNOLOGY_IMAGE,
        targetId: variables.technologyId,
        targetType: 'technology',
      },
      queryClient,
    );
  };

  return useMutation<AttachmentSelectDto, ApiErrorWrapper, AttachmentFeatureParams>({
    mutationFn: async (data) => {
      if (!isInnovator) {
        throw new Error('Only innovators can feature technology images.');
      }

      return await apiPost<AttachmentSelectDto>({
        path: `/v1/innovator/technologies/${data.technologyId}/attachments/${data.attachmentId}/feature`,
      });
    },
    onSuccess,
  });
};

export const useInnovatorClearTechnologyFeaturedImage = () => {
  const { apiDelete } = useApi();
  const { isInnovator } = useGetCurrentUser();
  const queryClient = useQueryClient();

  const onSuccess = (_: void, variables: AttachmentFeatureParams) => {
    invalidateAttachmentListQuery(
      {
        category: AttachmentCategory.TECHNOLOGY_IMAGE,
        targetId: variables.technologyId,
        targetType: 'technology',
      },
      queryClient,
    );
  };

  return useMutation<void, ApiErrorWrapper, AttachmentFeatureParams>({
    mutationFn: async (data) => {
      if (!isInnovator) {
        throw new Error('Only innovators can clear technology featured images.');
      }

      await apiDelete({
        path: `/v1/innovator/technologies/${data.technologyId}/attachments/${data.attachmentId}/feature`,
      });
    },
    onSuccess,
  });
};

// ---------------------------------------------------------------------------
// Utility functions
// ---------------------------------------------------------------------------

export async function innovatorUploadBufferedAttachments(params: {
  apiPost: <T>(p: { path: string }) => Promise<T>;
  fileAttachments: BufferedAttachment[];
  onError?: BufferedAttachmentErrorHandler;
  pendingUploads: PendingUploadDto[];
  technologyId: string;
}): Promise<void> {
  const { apiPost, fileAttachments, onError, pendingUploads, technologyId } = params;

  for (let i = 0; i < pendingUploads.length; i++) {
    const upload = pendingUploads[i];
    const pending = fileAttachments[i];

    if (!upload || !pending?.file) continue;

    try {
      const contentType = pending.contentType || 'application/octet-stream';

      const s3Response = await window.fetch(upload.uploadUrl, {
        body: pending.file,
        headers: { 'Content-Type': contentType },
        method: 'PUT',
      });

      if (!s3Response.ok) {
        const message = `Failed to upload "${pending.title}". You can retry from the technology edit page.`;

        reportAttachmentUploadError({
          context: {
            attachmentId: upload.attachmentId,
            category: pending.category,
            contentType,
            fileSize: pending.file.size,
            phase: 's3_put',
            status: s3Response.status,
            statusText: s3Response.statusText,
            targetId: technologyId,
            targetType: 'technology',
          },
          error: new Error(`S3 upload failed: ${s3Response.status} ${s3Response.statusText}`),
        });

        notifyBufferedUploadFailure({
          message,
          onError,
          title: pending.title,
        });

        continue;
      }

      try {
        await apiPost({
          path: `/v1/innovator/technologies/${technologyId}/attachments/${upload.attachmentId}/complete`,
        });

        if (pending.category === AttachmentCategory.TECHNOLOGY_IMAGE && pending.featured) {
          try {
            await apiPost({
              path: `/v1/innovator/technologies/${technologyId}/attachments/${upload.attachmentId}/feature`,
            });
          } catch (error) {
            const message = `Uploaded "${pending.title}", but failed to set it as featured. You can retry from the technology edit page.`;

            reportAttachmentUploadError({
              context: {
                attachmentId: upload.attachmentId,
                category: pending.category,
                contentType,
                fileSize: pending.file.size,
                phase: 'complete_upload',
                targetId: technologyId,
                targetType: 'technology',
              },
              error,
            });

            notifyBufferedUploadFailure({
              message,
              onError,
              title: pending.title,
            });
          }
        }
      } catch (error) {
        const message = `Failed to upload "${pending.title}". You can retry from the technology edit page.`;

        reportAttachmentUploadError({
          context: {
            attachmentId: upload.attachmentId,
            category: pending.category,
            contentType,
            fileSize: pending.file.size,
            phase: 'complete_upload',
            targetId: technologyId,
            targetType: 'technology',
          },
          error,
        });

        notifyBufferedUploadFailure({
          message,
          onError,
          title: pending.title,
        });
      }
    } catch (error) {
      const message = `Failed to upload "${pending.title}". You can retry from the technology edit page.`;

      reportAttachmentUploadError({
        context: {
          attachmentId: upload.attachmentId,
          category: pending.category,
          contentType: pending.contentType || 'application/octet-stream',
          fileSize: pending.file.size,
          phase: 's3_put',
          targetId: technologyId,
          targetType: 'technology',
        },
        error,
      });

      notifyBufferedUploadFailure({
        message,
        onError,
        title: pending.title,
      });
    }
  }
}
